/**
 * Unified Session Creation Service
 *
 * Centralizes all session creation logic to ensure consistent behavior
 * across different parts of the application (sidebar, agent cards, nav, etc.)
 */

import { ChatApiClient } from "@/lib/api/clients/chat-api-client";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { sessionRefreshService } from "@/lib/services/sessionRefreshService";

export interface SessionCreationOptions {
  router: AppRouterInstance;
  agentId: string;
  sessionName?: string;
  shouldNavigate?: boolean;
  customSuccessHandler?: (sessionId: string) => void;
  customErrorHandler?: (error: Error) => void;
  source?: string; // For debugging/analytics
}

export interface SessionCreationResult {
  success: boolean;
  sessionId: string | null;
  error?: Error;
}

class SessionCreationService {
  private static instance: SessionCreationService;
  private chatApiClient = new ChatApiClient();

  private constructor() {}

  static getInstance(): SessionCreationService {
    if (!SessionCreationService.instance) {
      SessionCreationService.instance = new SessionCreationService();
    }
    return SessionCreationService.instance;
  }

  /**
   * Create a new session with unified behavior
   */
  async createSession(
    options: SessionCreationOptions
  ): Promise<SessionCreationResult> {
    const {
      router,
      agentId,
      sessionName,
      shouldNavigate = true,
      customSuccessHandler,
      customErrorHandler,
      source = "unknown",
    } = options;

    if (!agentId) {
      const error = new Error("Agent ID is required for session creation");
      console.error(
        `[SessionCreationService] ${error.message} (source: ${source})`
      );
      customErrorHandler?.(error);
      return { success: false, sessionId: null, error };
    }

    try {
      console.log(
        `[SessionCreationService] Creating session for agent ${agentId} (source: ${source})`,
        {
          sessionName,
          shouldNavigate,
        }
      );

      // Create the session with backend
      const newSession = await this.chatApiClient.createChatSession(agentId, {
        name: sessionName || this.getDefaultSessionName(),
      });

      if (!newSession?.id) {
        throw new Error("Session creation failed: No session ID returned");
      }

      console.log(
        `[SessionCreationService] Session created successfully: ${newSession.id} (source: ${source})`
      );

      // Set flag to indicate new session creation for downstream components
      if (typeof window !== "undefined") {
        sessionStorage.setItem("isCreatingNewSession", "true");
        sessionStorage.setItem("newSessionSource", source);
      }

      // Navigate to the new session if requested
      if (shouldNavigate) {
        router.replace(`/agents/${agentId}/sessions/${newSession.id}`);
      }

      // Trigger sidebar refresh
      sessionRefreshService.refreshAfterNewSession({
        reason: `session created via SessionCreationService (${source})`,
        delay: 100,
      });

      // Call custom success handler
      customSuccessHandler?.(newSession.id);

      return {
        success: true,
        sessionId: newSession.id,
      };
    } catch (error) {
      const errorObj =
        error instanceof Error
          ? error
          : new Error("Unknown session creation error");
      console.error(
        `[SessionCreationService] Session creation failed for agent ${agentId} (source: ${source}):`,
        errorObj
      );

      customErrorHandler?.(errorObj);

      return {
        success: false,
        sessionId: null,
        error: errorObj,
      };
    }
  }

  /**
   * Create session and navigate (backward compatibility with existing sessionManager)
   */
  async createAndNavigateToNewSession(
    agentId: string,
    options: {
      router: AppRouterInstance;
      sessionName?: string;
      onSuccess?: (sessionId: string) => void;
      onError?: (error: Error) => void;
      onRefreshSidebarSessions?: () => void;
      source?: string;
    }
  ): Promise<{ sessionId: string | null; success: boolean }> {
    const result = await this.createSession({
      router: options.router,
      agentId,
      sessionName: options.sessionName,
      shouldNavigate: true,
      customSuccessHandler: options.onSuccess,
      customErrorHandler: options.onError,
      source: options.source || "legacy-sessionManager",
    });

    return {
      sessionId: result.sessionId,
      success: result.success,
    };
  }

  private getDefaultSessionName(): string {
    return "New Chat";
  }
}

// Export singleton instance
export const sessionCreationService = SessionCreationService.getInstance();

// Utility functions for easy access (maintaining backward compatibility)
export const createAndNavigateToNewSession = (
  agentId: string,
  options: {
    router: AppRouterInstance;
    sessionName?: string;
    onSuccess?: (sessionId: string) => void;
    onError?: (error: Error) => void;
    onRefreshSidebarSessions?: () => void;
    source?: string;
  }
) => sessionCreationService.createAndNavigateToNewSession(agentId, options);
